import React, { useState, useEffect, useRef } from 'react';
import { X, Calendar, User, Filter, Tag, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Category } from '@/services/api';
import { useDebouncedAuthors } from '@/hooks/useDebouncedAuthors';
import dayjs, { Dayjs } from 'dayjs';
import { getPlatformIcon, getPlatforms, getCategoryIcon } from "@/lib/utils";

export interface CategorySelection {
    id: string;
    category_enum: string;
    category_display_value: string;
    is_folder: boolean;
}

interface FilterPopupProps {
    isOpen: boolean;
    onClose: () => void;
    onApplyFilters: (filters: FilterState) => void;
    appliedFilters: FilterState;
    selectedCategory: CategorySelection | null; // Add selected category from sidebar
    categories: Category[];
}

export interface FilterState {
    authorName: string;
    dateFrom: string;
    dateTo: string;
    category: CategorySelection | null;
}

const FilterPopup: React.FC<FilterPopupProps> = React.memo(({
    isOpen,
    onClose,
    onApplyFilters,
    appliedFilters,
    selectedCategory,
    categories
}) => {


    const [filters, setFilters] = useState<FilterState>(appliedFilters);
    const [searchTerm, setSearchTerm] = useState(appliedFilters.authorName);
    const [categorySearchTerm, setCategorySearchTerm] = useState(appliedFilters.category?.category_display_value || '');
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [isCategorySelected, setIsCategorySelected] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(dayjs());

    // Use custom hook for debounced author search
    const {
        authors,
        loading,
        showDropdown,
        debouncedSearch,
        markAuthorSelected,
        clearAuthors,
        setShowDropdown,
        resetState
    } = useDebouncedAuthors(300);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const categoryDropdownRef = useRef<HTMLDivElement>(null);
    const datePickerRef = useRef<HTMLDivElement>(null);

    // Convert string dates to dayjs format
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
        appliedFilters.dateFrom ? dayjs(appliedFilters.dateFrom) : null,
        appliedFilters.dateTo ? dayjs(appliedFilters.dateTo) : null,
    ]);

    // Track previous isOpen state to detect when dialog actually opens
    const prevIsOpenRef = useRef(false);
    
    // Update filters and search term when component opens (only when isOpen changes from false to true)
    useEffect(() => {
        if (isOpen && !prevIsOpenRef.current) {
            setFilters(appliedFilters);
            setSearchTerm(appliedFilters.authorName);
            setCategorySearchTerm(appliedFilters.category?.category_display_value || '');
            setDateRange([
                appliedFilters.dateFrom ? dayjs(appliedFilters.dateFrom) : null,
                appliedFilters.dateTo ? dayjs(appliedFilters.dateTo) : null,
            ]);
            
            // Only reset the author search state if there's no pre-existing author
            if (!appliedFilters.authorName) {
                resetState();
            } else {
                // Don't reset state if we have an author, just mark as selected
                markAuthorSelected();
            }

            // Categories are now passed as props, no need to load them
        }
        prevIsOpenRef.current = isOpen;
    }, [isOpen, appliedFilters, resetState, markAuthorSelected]);

    // Update filters when appliedFilters change (but only if popup is already open)
    useEffect(() => {
        if (isOpen && prevIsOpenRef.current) {
            setFilters(appliedFilters);
            setSearchTerm(appliedFilters.authorName);
            setCategorySearchTerm(appliedFilters.category?.category_display_value || '');
            setDateRange([
                appliedFilters.dateFrom ? dayjs(appliedFilters.dateFrom) : null,
                appliedFilters.dateTo ? dayjs(appliedFilters.dateTo) : null,
            ]);
        }
    }, [appliedFilters, isOpen]);

    // Sync with selectedCategory from sidebar
    useEffect(() => {
        // Check if selectedCategory has changed
        const hasCategoryChanged = selectedCategory && (
            !filters.category ||
            selectedCategory.id !== filters.category.id ||
            selectedCategory.category_display_value !== filters.category.category_display_value ||
            selectedCategory.is_folder !== filters.category.is_folder
        );
        
        if (hasCategoryChanged) {
            setFilters(prev => ({
                ...prev,
                category: selectedCategory
            }));
            setCategorySearchTerm(selectedCategory.category_display_value);
        } else if (!selectedCategory && filters.category) {
            setFilters(prev => ({
                ...prev,
                category: null
            }));
            setCategorySearchTerm('');
        }
    }, [selectedCategory]);





    // Filter categories when search term changes
    useEffect(() => {
        // Don't filter if a category was just selected
        if (isCategorySelected) {
            return;
        }

        if (categories.length === 0) {
            setShowCategoryDropdown(false);
        }
    }, [categories, isCategorySelected, showCategoryDropdown]);

    // Show all categories when input is focused and empty
    const handleCategoryInputFocus = () => {
        // If there's a selected category and no search term, populate it
        if (!categorySearchTerm && filters.category) {
            setCategorySearchTerm(filters.category.category_display_value);
        }

        // Only show dropdown when user actually focuses the input
        setShowCategoryDropdown(true);
    };

    // Handle category input change - only for search functionality, not for filtering dropdown
    const handleCategoryInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setCategorySearchTerm(newValue);
        
        // Don't filter the dropdown - always show all categories when focused
        // The search term is just for user reference, not for filtering the dropdown
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
            if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
                setShowCategoryDropdown(false);
            }
            if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
                setShowDatePicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleAuthorSelect = (author: any) => {
        setFilters(prev => ({
            ...prev,
            authorName: author.name,
            authorId: author.username // Use username as ID since we don't have id field
        }));

        setSearchTerm(author.name); // Use the actual name instead of @username format
        setShowDropdown(false);
        markAuthorSelected();
    };

    const handleCategorySelect = (category: Category) => {
        // Convert Category to CategorySelection format
        const categorySelection: CategorySelection = {
            id: String(category.id),
            category_enum: category.category_enum,
            category_display_value: category.category_display_value,
            is_folder: category.is_folder
        };
        
        setFilters(prev => ({
            ...prev,
            category: categorySelection
        }));

        // Update UI state
        setCategorySearchTerm(category.category_display_value);
        setShowCategoryDropdown(false); // Keep dropdown closed
        
        // Set category as selected to prevent filtering
        setIsCategorySelected(true);
        
        // Reset the category selection flag after a short delay to prevent immediate filtering
        setTimeout(() => {
            setIsCategorySelected(false);
        }, 100);
    };

    const handleDateRangeChange = (newRange: [Dayjs | null, Dayjs | null]) => {
        setDateRange(newRange);
        const [start, end] = newRange;
        setFilters(prev => ({
            ...prev,
            dateFrom: start ? start.format('YYYY-MM-DD') : '',
            dateTo: end ? end.format('YYYY-MM-DD') : ''
        }));
    };

    // Helper functions for custom calendar
    const handleDateSelect = (date: Dayjs) => {
        const [start, end] = dateRange;
        
        if (!start || (start && end)) {
            // Start new selection
            handleDateRangeChange([date, null]);
        } else if (start && !end) {
            // Complete the range
            if (date.isBefore(start)) {
                handleDateRangeChange([date, start]);
            } else {
                handleDateRangeChange([start, date]);
            }
            // Auto-close date picker when range is complete
            setTimeout(() => {
                setShowDatePicker(false);
            }, 150); // Small delay for visual feedback
        }
    };

    const isDateInRange = (date: Dayjs) => {
        const [start, end] = dateRange;
        if (!start || !end) return false;
        return (date.isAfter(start, 'day') || date.isSame(start, 'day')) && 
               (date.isBefore(end, 'day') || date.isSame(end, 'day'));
    };

    const isDateSelected = (date: Dayjs) => {
        const [start, end] = dateRange;
        return (start && date.isSame(start, 'day')) || (end && date.isSame(end, 'day'));
    };

    const isDateDisabled = (date: Dayjs) => {
        return date.isAfter(dayjs(), 'day'); // Disable future dates
    };

    const generateCalendarDays = () => {
        const startOfMonth = currentMonth.startOf('month');
        const endOfMonth = currentMonth.endOf('month');
        const startDate = startOfMonth.startOf('week');
        const endDate = endOfMonth.endOf('week');
        
        const days = [];
        let date = startDate;
        
        while (date.isBefore(endDate) || date.isSame(endDate, 'day')) {
            days.push(date);
            date = date.add(1, 'day');
        }
        
        return days;
    };

    // Clear individual filters
    const handleClearAuthor = () => {
        setFilters(prev => ({
            ...prev,
            authorName: '',
            authorId: null
        }));
        setSearchTerm('');
        clearAuthors();
    };

    const handleClearCategory = () => {
        setFilters(prev => ({
            ...prev,
            category: null
        }));
        setCategorySearchTerm('');
        setIsCategorySelected(false);
    };

    const handleClearDateRange = () => {
        setFilters(prev => ({
            ...prev,
            dateFrom: '',
            dateTo: ''
        }));
        setDateRange([null, null]);
    };

    const handleClearAll = () => {
        const clearedFilters: FilterState = {
            authorName: '',
            dateFrom: '',
            dateTo: '',
            category: null
        };
        setFilters(clearedFilters);
        setSearchTerm('');
        setCategorySearchTerm('');
        setDateRange([null, null]);
        clearAuthors();
        setIsCategorySelected(false);
        onApplyFilters(clearedFilters);
        onClose(); // Close the popup after clearing all filters
    };

    const handleApply = () => {
        // Use the filters as selected by the user, don't override with sidebar
        onApplyFilters(filters);
        onClose();
    };

    const formatDateRange = () => {
        const [start, end] = dateRange;
        if (start && end) {
            return `${start.format('MMM DD')} - ${end.format('MMM DD, YYYY')}`;
        } else if (start) {
            return `From ${start.format('MMM DD, YYYY')}`;
        } else if (end) {
            return `Until ${end.format('MMM DD, YYYY')}`;
        }
        return '';
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                        <Filter className="w-5 h-5" />
                        Filters
                    </h2>
                    <div
                        onClick={onClose}
                        className="text-gray-400 p-2 rounded-lg hover:border hover:border-gray-500 hover:text-white cursor-pointer"
                    >
                        <X className="w-5 h-5" />
                    </div>
                </div>

                <div className="space-y-6">
                    {/* Author Filter */}
                    <div>
                        <Label htmlFor="author" className="text-white mb-2 block">
                            Author Name
                        </Label>
                        <div className="relative" ref={dropdownRef}>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <Input
                                    id="author"
                                    type="text"
                                    placeholder="Filter by author name..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setSearchTerm(newValue);

                                        // Clear authors if search term is empty
                                        if (!newValue.trim()) {
                                            clearAuthors();
                                            return;
                                        }

                                        // Use custom hook for debounced search
                                        debouncedSearch(newValue);
                                    }}
                                    className="pl-10 pr-10 bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                                />
                                {searchTerm && (
                                    <div
                                        onClick={handleClearAuthor}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                                    >
                                        <X className="w-4 h-4" />
                                    </div>
                                )}
                            </div>

                            {showDropdown && (
                                <div className="absolute top-full left-0 right-0 bg-gray-700 border border-gray-600 rounded-md mt-1 max-h-48 overflow-y-auto z-10">
                                    {loading ? (
                                        <div className="p-3 text-gray-400 text-center">Loading...</div>
                                    ) : authors.length > 0 ? (
                                        authors.map((author) => (
                                            <div
                                                key={author.name}
                                                onClick={() => handleAuthorSelect(author)}
                                                className="w-full text-left px-4 py-2 hover:bg-gray-600 text-white text-sm flex items-center justify-between cursor-pointer"
                                            >
                                                <div>
                                                    <div className="font-medium">{author.name}</div>
                                                    <div className="text-gray-400 text-xs">@{author.username}</div>
                                                </div>
                                                {(() => {
                                                    const platformMatch = author.platform
                                                        ? getPlatforms().find(p => p.api_value === String(author.platform))
                                                        : undefined;
                                                    if (!platformMatch) return null;
                                                    const Icon = getPlatformIcon(platformMatch.id);
                                                    return (
                                                        <span className="ml-3 flex-shrink-0">
                                                            {React.createElement(Icon, { className: 'w-4 h-4' })}
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                        ))
                                    ) : searchTerm.length >= 1 ? (
                                        <div className="p-3 text-gray-400 text-center">No authors found</div>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Category Filter */}
                    <div>
                        <Label htmlFor="category" className="text-white mb-2 block">
                            Category
                        </Label>
                        <div className="relative" ref={categoryDropdownRef}>
                            <div className="relative">
                                <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <Input
                                    id="category"
                                    type="text"
                                    placeholder="Filter by category..."
                                    value={categorySearchTerm}
                                    onChange={handleCategoryInputChange}
                                    onFocus={handleCategoryInputFocus}
                                    className="pl-10 pr-10 bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                                />
                                {categorySearchTerm && (
                                    <div
                                        onClick={handleClearCategory}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                                    >
                                        <X className="w-4 h-4" />
                                    </div>
                                )}
                            </div>

                            {showCategoryDropdown && (
                                <div className="absolute top-full left-0 right-0 bg-gray-700 border border-gray-600 rounded-md mt-1 max-h-48 overflow-y-auto z-10">
                                    {categories.length > 0 ? (
                                        categories.map((category) => (
                                                <div
                                                    key={`${category.id}-${category.category_display_value}-${category.is_folder}`}
                                                    onClick={() => handleCategorySelect(category)}
                                                    className="w-full text-left px-4 py-2 hover:bg-gray-600 text-white text-sm flex items-center justify-between cursor-pointer"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        {(() => {
                                                            const is_label = category.category_enum === "others" && category.category_display_value.toLowerCase() != "others"
                                                            const Icon = getCategoryIcon(category.category_enum, category.is_folder, is_label);
                                                            return <Icon className="w-4 h-4" />;
                                                        })()}
                                                        <span>{category.category_display_value}</span>
                                                    </div>
                                                    <span className="text-gray-400 text-xs">
                                                        {category.post_count}
                                                    </span>
                                                </div>
                                            ))
                                    ) : (
                                        <div className="p-3 text-gray-400 text-center">No categories available</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Date Range Filter */}
                    <div>
                        <Label className="text-white mb-2 block flex items-center gap-2">
                            Date Range
                        </Label>
                        <div className="relative" ref={datePickerRef}>
                            <div className="relative">
                                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <Input
                                    onClick={() => setShowDatePicker(!showDatePicker)}
                                    placeholder="Filter by date range..."
                                    value={formatDateRange()}
                                    readOnly
                                    className="pl-10 pr-10 bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                                />
                                {(dateRange[0] || dateRange[1]) && (
                                    <div
                                        onClick={handleClearDateRange}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                                    >
                                        <X className="w-4 h-4" />
                                    </div>
                                )}
                            </div>

                            {showDatePicker && (
                                <div className="absolute top-full left-0 right-0 bg-gray-800 border border-gray-700 rounded-md mt-1 p-4 z-10 shadow-xl">
                                    {/* Calendar Header */}
                                    <div className="flex items-center justify-between mb-4">
                                        <button
                                            onClick={() => setCurrentMonth(currentMonth.subtract(1, 'month'))}
                                            className="p-1 hover:bg-gray-700 bg-transparent rounded text-gray-400 hover:text-white"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                        </button>
                                        <h3 className="text-white font-semibold">
                                            {currentMonth.format('MMMM YYYY')}
                                        </h3>
                                        <button
                                            onClick={() => setCurrentMonth(currentMonth.add(1, 'month'))}
                                            className="p-1 hover:bg-gray-700 bg-transparent rounded text-gray-400 hover:text-white"
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {/* Week Day Labels */}
                                    <div className="grid grid-cols-7 gap-1 mb-2">
                                        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                                            <div key={day} className="text-center text-gray-400 text-sm font-medium py-2">
                                                {day}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Calendar Days */}
                                    <div className="grid grid-cols-7 gap-1">
                                        {generateCalendarDays().map((date) => {
                                            const isCurrentMonth = date.month() === currentMonth.month();
                                            const isSelected = isDateSelected(date);
                                            const inRange = isDateInRange(date);
                                            const isDisabled = isDateDisabled(date);
                                            const isToday = date.isSame(dayjs(), 'day');

                                            return (
                                                <button
                                                    key={date.format('YYYY-MM-DD')}
                                                    onClick={() => !isDisabled && handleDateSelect(date)}
                                                    disabled={isDisabled}
                                                    className={`
                                                        w-8 h-8 text-sm rounded flex items-center justify-center transition-colors
                                                        ${!isCurrentMonth ? 'text-gray-600' : 'text-gray-300'}
                                                        ${isDisabled ? 'text-gray-600 cursor-not-allowed' : 'cursor-pointer'}
                                                        ${isSelected ? 'bg-purple-600/20 text-white' : 
                                                          inRange ? 'bg-purple-400/20 text-white' :
                                                          isToday ? 'bg-gray-600/40 text-white' :
                                                          !isDisabled ? 'bg-transparent hover:bg-gray-700' : 'bg-transparent'}
                                                    `}
                                                >
                                                    {date.date()}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Quick Select Options */}
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        <button
                                            onClick={() => {
                                                const today = dayjs();
                                                const lastWeek = today.subtract(7, 'day');
                                                handleDateRangeChange([lastWeek, today]);
                                            }}
                                            className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded"
                                        >
                                            Last 7 days
                                        </button>
                                        <button
                                            onClick={() => {
                                                const today = dayjs();
                                                const lastMonth = today.subtract(30, 'day');
                                                handleDateRangeChange([lastMonth, today]);
                                            }}
                                            className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded"
                                        >
                                            Last 30 days
                                        </button>
                                        <button
                                            onClick={() => {
                                                const today = dayjs();
                                                const startOfMonth = today.startOf('month');
                                                handleDateRangeChange([startOfMonth, today]);
                                            }}
                                            className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded"
                                        >
                                            This month
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 mt-6">
                    <Button
                        variant="outline"
                        onClick={handleClearAll}
                        className="flex-1 border-gray-600 text-black hover:bg-gray-700 hover:text-white"
                    >
                        Clear All
                    </Button>
                    <Button
                        onClick={handleApply}
                        className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                    >
                        Apply Filters
                    </Button>
                </div>
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary re-renders
    const keys = Object.keys(prevProps) as (keyof FilterPopupProps)[];
    for (const key of keys) {
        if (key === 'appliedFilters') {
            // Deep comparison for appliedFilters object
            const prevFilters = prevProps[key];
            const nextFilters = nextProps[key];
            
            // Compare basic fields
            if (prevFilters.authorName !== nextFilters.authorName ||
                prevFilters.dateFrom !== nextFilters.dateFrom ||
                prevFilters.dateTo !== nextFilters.dateTo) {
                return false; // Re-render
            }
            
            // Compare category objects
            const prevCategory = prevFilters.category;
            const nextCategory = nextFilters.category;
            if (prevCategory === null && nextCategory === null) {
                // Both null, continue
            } else if (prevCategory === null || nextCategory === null) {
                return false; // One is null, other isn't - re-render
            } else if (prevCategory.id !== nextCategory.id ||
                      prevCategory.category_display_value !== nextCategory.category_display_value ||
                      prevCategory.is_folder !== nextCategory.is_folder) {
                return false; // Category changed - re-render
            }
        } else if (prevProps[key] !== nextProps[key]) {
            return false; // Re-render
        }
    }
    return true; // Don't re-render
});

export default FilterPopup;
